import { Box, Divider, Grid2, Typography } from "@mui/material";
import * as React from "react";
import TwinHierarchyCard from "./TwinHierarchyCard";
import VitalsChartSection from "./VitalsChartSection";
import InstantaneousMatrix from "./InstantaneousMatrix";
import ComparisonView from "./ComparisonChart/ComparisonView";
import { useTranslation } from "react-i18next";
import { useBatteryConfig, useBatteryInfo, useBatterySystem, useVehicleInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { DigitalTwinTabContainer } from "../../../../Theme/DigitalTwinStyles";
import { getVehicleInfo } from "../../../../Api/VehicleApi";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import { fetchDigitalTwinHierarchy, getHierarchyEntityTypes } from "../../../../Helper/DigitalTwin/DigitalTwinFunctions";

export default function BatteryVitals() {
    const { batteryID, batteryInfo, showBusDeviceInfo } = useBatteryInfo();
    const batteryConfig = useBatteryConfig();
    const batterySystem = useBatterySystem();
    const pageContent = usePagesContent()
    const [dataGapDates, setDataGapDates] = React.useState(batteryInfo?.dataGaps || [])

    const [batterySystemForComparison, setBatterySystemForComparison] = React.useState([]);
    const [digitalTwinHierarchy, setDigitalTwinHierarchy] = React.useState([]);

    const { t } = useTranslation(['common', 'digital_twin']);
    const [selectedNode, setSelectedNode] = React.useState(null);
    const vehicleInfo = useVehicleInfo();

    const prepareDigitalTwinHierarchy = async () => {
        const entityTypes = getHierarchyEntityTypes(pageContent);
        const digitalTwinHierarchyResponse = await fetchDigitalTwinHierarchy(batteryID, entityTypes, t);
        setDigitalTwinHierarchy(digitalTwinHierarchyResponse);
    }

    React.useEffect(() => {
        prepareDigitalTwinHierarchy();
    }, [pageContent]);

    React.useEffect(() => {
        if (!Object.keys(digitalTwinHierarchy).length) {
            return;
        }
        const entityTypes = getHierarchyEntityTypes(pageContent);
        const hierarchyKey = entityTypes.includes('BATTERY_SYSTEM') ? 'BATTERY_SYSTEM' : 'BATTERY_STRUCTURE';

        let filteredBatterySystem = digitalTwinHierarchy[hierarchyKey];

        if (entityTypes.includes('BATTERY_SYSTEM')) {
        // remove first level, i.e., battery level from the hierarchy
            filteredBatterySystem = digitalTwinHierarchy[hierarchyKey]
                .filter(item => item.level === 'battery')
                .flatMap(item => item.children || []);
        }

        setBatterySystemForComparison(filteredBatterySystem);
    }, [digitalTwinHierarchy])

    return (
        <DigitalTwinTabContainer gap="16px">
            <Grid2 container flexDirection="column" gap="4px" paddingTop={'4px'} paddingBottom={'20px'} paddingLeft={'12px'}>
                <Typography variant="fw500s16lh20c000000"> {t(`digital_twin:compare_vital_trends`)} </Typography>
                <Typography variant="fw400s12c686868">{t(`digital_twin:compare_vital_trends_subtitle`)}</Typography>
            </Grid2>

            <Grid2 container sx={{
                borderRadius: '14px',
                border: '1px solid #E6E6E6',
                backgroundColor: '#FFF',
                minHeight: '600px',
                flexWrap: 'nowrap'
            }} >
                <Grid2 sx={{ width: '20%', flexShrink: 0, borderRight: '1px solid #E6E6E6' }}>
                    <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '90%', pb: 8}}>
                        <TwinHierarchyCard
                            title="Battery System"
                            currentNode={selectedNode}
                            onChangeNode={(node) => setSelectedNode(node)}
                            digitalTwinHierarchy={digitalTwinHierarchy}
                        />
                        <Box sx={{px: 2.5}}>
                            <Divider sx={{my: 2, borderColor: '#E9E9E9'}} />
                            <ComparisonView
                                batteryStructure={batterySystemForComparison}
                                batteryID={batteryID}
                                batteryInfo={batteryInfo}
                                batteryConfig={batteryConfig}
                                showBusDeviceInfo={showBusDeviceInfo}
                                vehicleInfo={vehicleInfo}
                                dataGapDates={dataGapDates}
                            />
                        </Box>
                    </Box>
                </Grid2>

                {selectedNode && (
                    <Grid2 flexGrow={1}>
                        <VitalsChartSection
                            selectedNode={selectedNode}
                            batteryID={batteryID}
                            batteryInfo={batteryInfo}
                            vehicleInfo={vehicleInfo}
                            dataGapDates={dataGapDates}
                        />
                    </Grid2>
                )}
            </Grid2>


            <Grid2 container flexDirection="column" gap="4px" paddingTop={'20px'} paddingLeft={'12px'}>
                <Typography variant="fw500s16lh20c000000">{t(`digital_twin:level_battery_system_title`)}</Typography>
                <Typography variant="fw400s12c686868">{t(`digital_twin:level_battery_system_subtitle`)}</Typography>
            </Grid2>

            <Grid2 sx={{ mb: 10 }}>
                <InstantaneousMatrix
                    batterySystem={batterySystem}
                    batteryInfo={batteryInfo}
                    vehicleInfo={vehicleInfo}
                    showBusDeviceInfo={showBusDeviceInfo}
                    batteryID={batteryID} />
            </Grid2>
        </DigitalTwinTabContainer >
    )
}
